import * as R from "ramda";
import React, {
  useRef,
  useMemo,
  useEffect,
  useCallback,
  useState
} from "react";
import { DragDropContext } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { connect, useDispatch } from "react-redux";
import { Route, withRouter, Redirect, Switch } from "react-router-dom";
import { compose } from "recompose";
import { notification, Modal, Popconfirm, Icon } from "antd";

import * as gtag from "../../../../lib/gtag";
import ActionsSnackbar, {
  SELECT_ALL_STATUS
} from "../../../../components/ActionsSnackbar/ActionsSnackbar";
import {
  BATCH_ACTIONS,
  DEFAULT_PROCESS_STEPS
} from "../../../../constants/opportunity";
import {
  processBatchAction,
  processBatchInvite,
  resetFilters,
  resetSelectedCandidates,
  selectCandidate,
  selectAllCandidates,
  setStepFilters
} from "../../../../store/actions/process";
import { pluralsForList } from "../../../../utils/strings";
import {
  resetOpportunityNotifications,
  updateOpportunitySteps
} from "../../../../store/actions/opportunity";
import uploadChatbotDocuments from "../../../../components/Chatbot/UploadChatbotDocuments";
import { moveCandidatesToRejectedConfirm } from "../../../../utils/batchActions";
import CandidatesTagsModal from "../OpportunityCandidates/CandidatesTagsModal";

import CandidateDetails from "./CandidateDetails";
import { Provider as ProcessProvider } from "./context";
import { useProcessActions, useProcessState, useProcessContext } from "./hooks";
import ScrollArrows from "./ScrollArrows";
import {
  getAvailableSteps,
  getChatbotStatus,
  filterMatches
} from "./selectors";
import Step from "./Step";
import ProcessEditor from "./ProcessEditor/ProcessEditor";
import { MoveToNextStepForm } from "./StepPopover";

import styled from "styled-components/macro";

const getStepFromMatch = (steps, matchId) =>
  R.pipe(
    R.keys,
    R.find(stepKey =>
      R.includes(matchId, R.map(R.prop("id"), steps[stepKey].matches))
    )
  )(steps);

const mapIndexed = R.addIndex(R.map);

export const Container = styled.div`
  background: #9ca8b1;
  flex-grow: 1;
  padding: 12px 12px 0 12px;
  width: 100vw;
  height: 50vh;
  display: flex;
  position: relative;
  -ms-overflow-style: -ms-autohiding-scrollbar;
  margin-top: 16px;
  padding: 20px 10px;
  align-self: center;
  /* max-width: 1168px; */
`;

const Overflow = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  margin-bottom: 3px;

  ::-webkit-scrollbar {
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #00000017;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: #00000052;
  }
`;

const HEADER_HEIGHT = "140px";
export const ProcessContent = styled.div`
  display: flex;
  height: 100%;
  max-height: calc(100vh - ${HEADER_HEIGHT});
`;

export const Steps = styled.div`
  height: 100%;
  max-height: inherit;
  min-width: 300px;
  width: 300px;
  border-radius: 3px;

  &:not(:last-child) {
    margin-right: 12px;
  }
`;

const OpportunityProcess = ({
  history,
  canMove,
  canChat,
  match: routeMatch,
  opportunity,
  processBatchAction,
  processBatchInvite,
  resetFilters,
  resetSelectedCandidates,
  selectAllCandidates,
  selectCandidate,
  selectedCandidates,
  selectedCandidatesStep,
  filters,
  setStepFilters
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["opportunity", "global", "toasts"]);

  const containerRef = useRef();
  const {
    matches,
    steps,
    stepsOrder,
    targets,
    draggingStep,
    draggingMatch,
    stepPagination,
    status
  } = useProcessState();
  const {
    startDrag,
    endDrag,
    moveMatch,
    removeMatch,
    updateStep,
    sendOffer,
    loadStepMatches,
    refreshProcess
  } = useProcessActions();
  const getProcessContext = useProcessContext();
  const [processHasChanged, setProcessHasChanged] = useState(false);
  const [candidatesTagsModalVisible, setCandidatesTagsModalVisible] = useState(
    false
  );

  const filteredSteps = React.useMemo(() => {
    return R.map(
      step => ({
        ...step,
        matches: filterMatches(
          step,
          filters[step.id],
          step.matches || [],
          opportunity.successProbabilityProfile
        )
      }),
      steps
    );
  }, [steps, filters, opportunity.successProbabilityProfile]);

  // TODO FIX i18n interpolations to use the <Trans> Component instead of this hack
  const plurals = useMemo(() => pluralsForList(selectedCandidates), [
    selectedCandidates
  ]);

  const lastStep = useMemo(
    () =>
      R.find(R.propEq("name", DEFAULT_PROCESS_STEPS.accepted))(
        R.values(filteredSteps)
      ),
    [filteredSteps]
  );

  useEffect(() => {
    dispatch(
      resetOpportunityNotifications({
        id: opportunity.id,
        type: "match"
      })
    );
  }, [opportunity.id, dispatch]);

  useEffect(() => {
    return () => {
      resetSelectedCandidates();
      resetFilters();
    };
  }, [resetSelectedCandidates, opportunity.id, resetFilters]);

  useEffect(() => {
    gtag.event({
      category: "Process",
      action: `Process`,
      label: opportunity.id
    });
  }, [opportunity.id]);

  const selectedCandidatesStepMatches = useMemo(
    () =>
      R.reject(
        R.prop("loading"),
        R.pathOr(
          [],
          [R.prop("id", selectedCandidatesStep), "matches"],
          filteredSteps
        )
      ),
    [selectedCandidatesStep, filteredSteps]
  );

  const handleSelectAllChange = React.useCallback(
    e =>
      selectAllCandidates({
        isChecked: e.target.checked,
        candidates: selectedCandidatesStepMatches
      }),
    [selectAllCandidates, selectedCandidatesStepMatches]
  );

  const selectAllStatus = React.useMemo(
    () =>
      selectedCandidates.length >= 1 &&
      selectedCandidates.length === R.length(selectedCandidatesStepMatches)
        ? SELECT_ALL_STATUS.CHECKED
        : selectedCandidates.length >= 1
        ? SELECT_ALL_STATUS.INDETERMINATE
        : SELECT_ALL_STATUS.UNCHECKED,
    [selectedCandidates.length, selectedCandidatesStepMatches]
  );

  const handleProcessBatchAction = useCallback(
    payload => () =>
      processBatchAction({
        candidates: selectedCandidates,
        ...payload,
        step: selectedCandidatesStep,
        getContext: getProcessContext
      }),
    [
      getProcessContext,
      processBatchAction,
      selectedCandidates,
      selectedCandidatesStep
    ]
  );

  const handleProcessBatchInvite = useCallback(
    payload => () =>
      processBatchInvite({
        candidates: selectedCandidates,
        ...payload,
        step: selectedCandidatesStep,
        getContext: getProcessContext
      }),
    [
      getProcessContext,
      processBatchInvite,
      selectedCandidates,
      selectedCandidatesStep
    ]
  );

  const selectedCandidatesStepIndex = useMemo(
    () =>
      Boolean(selectedCandidatesStep) &&
      selectedCandidatesStep.id !== lastStep.id &&
      R.findIndex(R.equals(R.prop("id", selectedCandidatesStep)), stepsOrder),
    [selectedCandidatesStep, lastStep, stepsOrder]
  );

  // TODO add invite
  const externalSelectedCandidates = useMemo(
    () => R.reject(R.propOr(true, "isRegistered"), selectedCandidates),
    [selectedCandidates]
  );

  const snackbarActions = useMemo(
    () => [
      {
        "data-test": "multiple-candidates-tags",
        description: t(`opportunity:candidates.multipleSelection.tags`),
        icon: { type: "tags" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: "Process.Bulk.tags"
          });
          setCandidatesTagsModalVisible(true);
        },
        enabled: true
      },
      {
        "data-test": "multiple-candidates-move-to-next-step",
        description: t(
          "opportunity:candidates.multipleSelection.moveCandidatesToNextStep"
        ),
        icon: { type: "double-right" },
        popover: (
          <MoveToNextStepForm
            steps={getAvailableSteps(
              selectedCandidatesStepIndex,
              stepsOrder,
              filteredSteps
            )}
            step={selectedCandidatesStep}
            opportunity={opportunity}
            matches={selectedCandidates}
            onSubmit={(nextStepId, nextStep) => {
              gtag.event({
                category: "Bulk",
                action: "Process.Bulk.moveToStep"
              });
              handleProcessBatchAction({
                action: BATCH_ACTIONS.process.moveToStep,
                nextStep
              })();
            }}
          />
        ),
        enabled:
          canMove &&
          R.not(R.eqBy(R.prop("id"), selectedCandidatesStep, lastStep))
      },
      {
        "data-test": "multiple-candidates-move-to-rejected",
        description: t(
          "opportunity:candidates.multipleSelection.moveCandidatesToRejected"
        ),
        icon: { type: "minus-circle" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: `Process.Bulk.moveToRejected`
          });
          moveCandidatesToRejectedConfirm({
            count: R.length(selectedCandidates),
            plurals,
            onOk: handleProcessBatchAction({
              action: BATCH_ACTIONS.process.moveToRejected
            })
          });
        },
        enabled: canMove
      },
      {
        "data-test": "multiple-candidates-send-invite",
        description: t(
          "opportunity:candidates.multipleSelection.inviteCandidates"
        ),
        icon: { type: "mail" },
        onClick: () => {
          gtag.event({
            category: "Bulk",
            action: `Process.Bulk.inviteToWorcket`
          });
          handleProcessBatchInvite({
            action: BATCH_ACTIONS.process.sendInvite,
            candidates: externalSelectedCandidates
          })();
        },
        enabled: R.eqBy(
          R.length,
          externalSelectedCandidates,
          selectedCandidates
        )
      }
    ],
    [
      canMove,
      externalSelectedCandidates,
      filteredSteps,
      handleProcessBatchAction,
      handleProcessBatchInvite,
      lastStep,
      opportunity,
      plurals,
      selectedCandidates,
      selectedCandidatesStep,
      selectedCandidatesStepIndex,
      stepsOrder,
      t
    ]
  );

  const saveProcess = useCallback(
    async newSteps => {
      try {
        const orderedSteps = R.pipe(
          mapIndexed((step, i) => R.assoc("order", i + 1, step))
        )(newSteps);

        await uploadChatbotDocuments(
          opportunity.id,
          R.find(R.propEq("type", "chatbot"), orderedSteps),
          dispatch
        );

        await dispatch(
          updateOpportunitySteps({
            steps: orderedSteps,
            opportunityId: opportunity.id
          })
        );

        refreshProcess();
        history.push(routeMatch.url);
      } catch (e) {
        console.error("update failed", e);
      }
    },
    [dispatch, history, opportunity.id, refreshProcess, routeMatch.url]
  );

  return (
    <>
      <Container>
        <Overflow ref={containerRef}>
          <ProcessContent>
            <ScrollArrows containerRef={containerRef} />
            <DragDropContext onDragEnd={endDrag} onDragStart={startDrag}>
              {stepsOrder.map((_step, index) => {
                const step = filteredSteps[_step];
                const originalStep = steps[_step];
                const currentStepPagination = stepPagination[step.id] || {};
                const loadedMatchesCount = R.length(
                  R.propOr([], "matches", originalStep)
                );
                const totalMatchesCount = R.propOr(
                  Math.max(
                    loadedMatchesCount,
                    R.length(R.propOr([], "columnOrder", originalStep))
                  ),
                  "total",
                  currentStepPagination
                );

                const hasKnownTotal = !R.isNil(
                  R.prop("total", currentStepPagination)
                );
                const displayTotalMatchesCount = Math.max(
                  loadedMatchesCount,
                  hasKnownTotal
                    ? totalMatchesCount
                    : R.length(R.propOr([], "columnOrder", originalStep))
                );

                return (
                  <Steps key={step.id}>
                    <Step
                      selectCandidate={selectCandidate}
                      selectedCandidates={R.map(
                        R.prop("id"),
                        selectedCandidates
                      )}
                      selectedCandidatesStep={selectedCandidatesStep}
                      opportunity={opportunity}
                      isLoading={status === "pending"}
                      isDraggingFrom={
                        Boolean(draggingStep) && draggingStep !== step.id
                      }
                      canDropTo={R.includes(step.id, targets)}
                      openPositions={opportunity.positions}
                      match={matches[draggingMatch]}
                      step={step}
                      stepPagination={currentStepPagination}
                      loadedMatchesCount={loadedMatchesCount}
                      totalMatchesCount={displayTotalMatchesCount}
                      loadStepMatches={loadStepMatches}
                      previousStep={filteredSteps[stepsOrder[index - 1]]}
                      nextStep={filteredSteps[stepsOrder[index + 1]]}
                      lastStep={lastStep}
                      order={stepsOrder}
                      index={index}
                      removeMatch={removeMatch}
                      canMove={canMove}
                      canChat={canChat}
                      moveToNextStep={moveMatch}
                      updateStep={updateStep}
                      sendOffer={sendOffer}
                      filters={filters[step.id]}
                      setFilters={filters => {
                        setStepFilters({
                          stepId: step.id,
                          filters,
                          // Filter selected candidates based on new filters (only if selection is enabled)
                          ...(selectedCandidates.length &&
                          selectedCandidatesStep.id === step.id
                            ? {
                                newSelectedCandidates: filterMatches(
                                  selectedCandidatesStep,
                                  filters,
                                  selectedCandidates,
                                  opportunity.successProbabilityProfile
                                )
                              }
                            : {})
                        });
                      }}
                      matches={matches}
                      onEdit={() => {
                        gtag.event({
                          category: "Process",
                          action: `Process-Edit`,
                          label: opportunity.id
                        });
                        resetSelectedCandidates();
                        history.push(`${routeMatch.url}/editar/${step.id}`);
                      }}
                    />
                  </Steps>
                );
              })}
            </DragDropContext>
          </ProcessContent>
        </Overflow>
        <Switch>
          <Route
            path={`${routeMatch.url}/editar/:stepId?`}
            render={({ match }) => {
              return (
                <Modal
                  bodyStyle={{ padding: 0 }}
                  width={900}
                  visible={true}
                  onCancel={() =>
                    !processHasChanged && history.push(`${routeMatch.url}`)
                  }
                  closeIcon={
                    processHasChanged ? (
                      <Popconfirm
                        title={t("opportunity:process.processEditor.sureClose")}
                        onConfirm={() => history.push(`${routeMatch.url}`)}
                        okText={t("global:yes")}
                        cancelText={t("global:no")}
                      >
                        <Icon type="close" />
                      </Popconfirm>
                    ) : (
                      <Icon type="close" />
                    )
                  }
                  footer={null}
                  maskClosable={!processHasChanged}
                >
                  <ProcessEditor
                    initialSteps={Object.values(steps)}
                    initialEditStepId={match.params.stepId}
                    updateStep={updateStep}
                    onChangeEdit={stepId => {
                      history.replace(
                        `${routeMatch.url}/editar${stepId ? `/${stepId}` : ""}`
                      );
                    }}
                    onSubmit={saveProcess}
                    setProcessHasChanged={setProcessHasChanged}
                  />
                </Modal>
              );
            }}
          />
          <Route
            path={`${routeMatch.url}/:candidateId/:tab?`}
            render={({ match, location }) => {
              const candidateId = match.params.candidateId;
              const stepId = getStepFromMatch(steps, candidateId);
              const stepMatches = R.pathOr([], [stepId, "matches"], steps);
              const candidateIndex = R.findIndex(
                R.propEq("id", candidateId),
                stepMatches
              );
              const candidate = stepMatches[candidateIndex];

              if (!candidate) {
                notification.error({
                  message: t("global:error"),
                  description: t("toasts:candidates.loadMatchDetailsRejected")
                });
                return <Redirect to={routeMatch.path} />;
              }

              const stepIndex = R.findIndex(R.equals(stepId), stepsOrder);
              const step = steps[stepId];

              const onNext =
                stepMatches[candidateIndex + 1] &&
                (() => {
                  history.push(
                    `${routeMatch.url}/${stepMatches[candidateIndex + 1].id}/info`
                  );
                });

              const onPrev =
                stepMatches[candidateIndex - 1] &&
                (() => {
                  history.push(
                    `${routeMatch.url}/${stepMatches[candidateIndex - 1].id}/info`
                  );
                });

              return (
                <CandidateDetails
                  onSelect={selected =>
                    selectCandidate({
                      candidate: matches[candidateId],
                      selected,
                      step
                    })
                  }
                  isSelected={Boolean(
                    R.find(R.propEq("id", candidateId), selectedCandidates)
                  )}
                  key={candidateId}
                  isRegistered={R.propOr(true, "isRegistered", candidate)}
                  candidateId={candidateId}
                  closeModal={() => history.push(routeMatch.url)}
                  canMove={canMove}
                  canChat={canChat}
                  stepsOrder={stepsOrder}
                  steps={steps}
                  step={steps[stepId]}
                  availableSteps={getAvailableSteps(
                    stepIndex,
                    stepsOrder,
                    steps
                  )}
                  stepId={stepId}
                  routeMatch={match}
                  location={location}
                  moveMatch={moveMatch}
                  chatbotStatus={getChatbotStatus(step, candidate)}
                  chatbotTags={matches[candidateId].chatbotTags}
                  handleRemoveMatch={() =>
                    removeMatch(stepId, match.params.candidateId)
                  }
                  returnToProcess={() => {
                    history.push(routeMatch.url);
                  }}
                  opportunity={opportunity}
                  onNext={onNext}
                  onPrev={onPrev}
                />
              );
            }}
          />
        </Switch>
      </Container>
      <CandidatesTagsModal
        onClose={() => setCandidatesTagsModalVisible(false)}
        visible={candidatesTagsModalVisible}
        candidates={selectedCandidates}
      />
      <ActionsSnackbar
        actions={snackbarActions}
        actionsTitle={`${R.length(selectedCandidates)}`}
        actionsDescription={t(
          `opportunity:candidates.multipleSelection.${
            R.equals(1, R.length(selectedCandidates))
              ? "selectedCandidate"
              : "selectedCandidates"
          }`
        )}
        onSelectAll={handleSelectAllChange}
        selectAllStatus={selectAllStatus}
        onClose={resetSelectedCandidates}
        visible={R.not(R.isEmpty(selectedCandidates))}
      />
    </>
  );
};

const OpportunityProcessContainer = props => (
  <ProcessProvider>
    <OpportunityProcess {...props} />
  </ProcessProvider>
);

export default compose(
  withRouter,
  connect(
    ({ opportunity, process }) => ({
      opportunity: opportunity.selected,
      selectedCandidates: process.selectedCandidates,
      selectedCandidatesStep: process.selectedCandidatesStep,
      filters: process.filters
    }),
    {
      processBatchAction,
      processBatchInvite,
      resetSelectedCandidates,
      resetFilters,
      selectAllCandidates,
      selectCandidate,
      setStepFilters
    }
  )
)(OpportunityProcessContainer);
